import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  alpha,
  useTheme,
  Fab,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Language as LanguageIcon,
  Preview as PreviewIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { questionsApi } from '../../api/questionsApi';
import { Language, QuestionDto, QuestionType } from '../../types/questions';
import LoadingSpinner from '../../components/LoadingSpinner';
import QuestionCard from '../../components/questions/QuestionCard';
import QuestionFormModal from '../../components/questions/QuestionFormModal';
import QuestionPreviewModal from '../../components/questions/QuestionPreviewModal';

const PersonalQuestionsEditor: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const selectedLanguage = Language.EN; // English only

  const [questions, setQuestions] = useState<QuestionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [openQuestionModal, setOpenQuestionModal] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<QuestionDto | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, [selectedLanguage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const questionsData = await questionsApi.getPersonalQuestions(selectedLanguage);
      setQuestions(questionsData.sort((a, b) => a.questionNumber - b.questionNumber));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setQuestions((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);

        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // Update question numbers
        return newItems.map((item, index) => ({
          ...item,
          questionNumber: index + 1,
        }));
      });
      setHasChanges(true);
    }
  };


  const handleAddQuestion = () => {
    setEditingQuestion(null);
    setOpenQuestionModal(true);
  };

  const handleEditQuestion = (question: QuestionDto) => {
    setEditingQuestion(question);
    setOpenQuestionModal(true);
  };

  const handleDeleteQuestion = async (questionId: number) => {
    try {
      setDeleting(questionId);
      await questionsApi.deleteQuestion(questionId);
      setQuestions(questions.filter(q => q.id !== questionId));
      setHasChanges(true);
      setSnackbar({
        open: true,
        message: 'Question archived successfully',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Error archiving question:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to archive question';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setDeleting(null);
    }
  };

  const handleSaveQuestion = async (questionData: Partial<QuestionDto>) => {
    try {
      if (editingQuestion && editingQuestion.id) {
        // Update existing question - remove id from the data being sent
        const { id, ...updateData } = questionData;
        const updated = await questionsApi.updateQuestion(editingQuestion.id, updateData);
        setQuestions(questions.map(q => q.id === editingQuestion.id ? updated : q));
        setSnackbar({
          open: true,
          message: 'Question updated successfully',
          severity: 'success'
        });
      } else {
        // Create new question with unique questionKey
        const timestamp = Date.now();
        const randomSuffix = Math.random().toString(36).substring(2, 8);
        const uniqueQuestionKey = questionData.questionKey || 
          `PERSONAL_${timestamp}_${randomSuffix}`.toUpperCase();
        
        const newQuestion = await questionsApi.createQuestion({
          ...questionData,
          questionKey: uniqueQuestionKey,
          professionKey: null,
          language: selectedLanguage,
          questionType: QuestionType.PERSONAL,
          questionNumber: questions.length + 1,
          questionVersion: 1,
        } as any);
        setQuestions([...questions, newQuestion]);
        setSnackbar({
          open: true,
          message: 'Question created successfully',
          severity: 'success'
        });
      }
      setHasChanges(true);
      setOpenQuestionModal(false);
    } catch (error: any) {
      console.error('Error saving question:', error);
      const errorMessage = error.response?.data?.message || error.message || 
        (editingQuestion ? 'Failed to update question' : 'Failed to create question');
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  const handleSaveOrder = async () => {
    try {
      setSaving(true);
      await questionsApi.reorderQuestions({
        language: selectedLanguage,
        questionType: QuestionType.PERSONAL,
        questionOrders: questions.map(q => ({
          id: q.id!,
          questionNumber: q.questionNumber,
        })),
      });
      setHasChanges(false);
      setSnackbar({
        open: true,
        message: 'Question order saved successfully',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Error saving question order:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save question order';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const questionIds = useMemo(() => questions.map(q => q.id || 0), [questions]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3, pb: 10 }}>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)` }}>
        <Stack spacing={2}>
          <Breadcrumbs>
            <Link
              component="button"
              variant="body1"
              onClick={() => navigate('/questions')}
              sx={{ textDecoration: 'none', color: 'text.primary', cursor: 'pointer' }}
            >
              Questions
            </Link>
            <Typography color="text.primary">Personal Questions</Typography>
          </Breadcrumbs>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Personal Questions
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip label={`${questions.length} questions`} size="small" color="info" />
                <Chip label="English Only" size="small" icon={<LanguageIcon />} color="primary" />
                {hasChanges && (
                  <Chip label="Unsaved changes" size="small" color="warning" />
                )}
              </Stack>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={() => setOpenPreviewModal(true)}
              >
                Preview
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSaveOrder}
                disabled={!hasChanges || saving}
              >
                {saving ? 'Saving...' : 'Save Order'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        Personal questions are asked to all workers regardless of their profession. These typically include basic information like name, contact details, and general qualifications.
      </Alert>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={questionIds}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={2}>
            {questions.map((question) => (
              <QuestionCard
                key={question.id}
                question={question}
                onEdit={handleEditQuestion}
                onDelete={handleDeleteQuestion}
                isDeleting={deleting === question.id}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>

      {questions.length === 0 && (
        <Box
          sx={{
            mt: 4,
            p: 4,
            textAlign: 'center',
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.5),
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No personal questions yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Start by adding personal questions that apply to all workers
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddQuestion}
          >
            Add First Question
          </Button>
        </Box>
      )}

      <Tooltip title="Add new question" placement="left">
        <Fab
          color="info"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
          onClick={handleAddQuestion}
        >
          <AddIcon />
        </Fab>
      </Tooltip>

      <QuestionFormModal
        open={openQuestionModal}
        onClose={() => setOpenQuestionModal(false)}
        onSave={handleSaveQuestion}
        question={editingQuestion}
        professionKey=""
        language={selectedLanguage}
      />

      <QuestionPreviewModal
        open={openPreviewModal}
        onClose={() => setOpenPreviewModal(false)}
        questions={questions}
        professionName="Personal Information"
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PersonalQuestionsEditor;